import { FileUploadGallery, FugFile, UploadedFile } from '@campfire/file-upload-gallery';
import { HoverText } from '@campfire/hover-link';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  OutlinedInput,
  OutlinedInputProps,
  Radio,
  RadioGroup,
  Select,
  SelectProps,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { AlternateEmailRounded, PhoneIphoneRounded, AccessTime, Link as LinkIcon } from '@material-ui/icons';
import {
  KeyboardDatePicker,
  KeyboardDatePickerProps,
  KeyboardTimePicker,
  KeyboardTimePickerProps,
} from '@material-ui/pickers';
import { Autocomplete, Rating } from '@material-ui/lab';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import React, { useEffect, useMemo, useState } from 'react';
import { TaskFormValuesType } from '../../form/task-form/model/task-form-init';
import { AddressAutoComplete, AddressAutoCompleteType } from '../AddressAutoComplete';
import { AddressManualFields, AddressManualFieldsType } from '../AddressManualFields';
import { TaskFieldContainer, TaskFieldContainerProps } from '../containers/TaskFieldContainer';
import { TaskFormikDropdownType, TaskFormikMultipleChoiceType, TaskFormikQuizType } from './TaskFormikFields';
import { SignaturePad } from '../SignaturePad';

const MAX_FIELD_WIDTH = 540;
const MAX_FIELD_WIDTH_SMALL = 360;

type TaskFieldCommonConfig = {
  required?: boolean;
  autoComplete?: string;
  error?: boolean;
  ariaId: string;
  ariaDescribedBy: string;
};

type ShortTextTaskFieldConfig = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type ShortTextTaskFieldProps = ShortTextTaskFieldConfig & TaskFieldContainerProps;

const ShortTextTaskField = (props: ShortTextTaskFieldProps) => {
  const {
    error,
    ariaId,
    ariaDescribedBy,
    value,
    onChange,
    required,
    autoComplete,
    outlinedInputProps,
    ...rest
  } = props;

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        fullWidth
        style={{ maxWidth: MAX_FIELD_WIDTH }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='Write your answer here'
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
      />
    </TaskFieldContainer>
  );
};

type LongTextTaskFieldConfig = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type LongTextTaskFieldProps = LongTextTaskFieldConfig & TaskFieldContainerProps;

const LongTextTaskField = (props: LongTextTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, outlinedInputProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        multiline
        rows={3}
        fullWidth
        style={{ maxWidth: MAX_FIELD_WIDTH }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='Write your answer here'
        value={value}
        onChange={onChange}
        required={required}
      />
    </TaskFieldContainer>
  );
};

type NumberTaskFieldConfig = {
  value: number;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type NumberTaskFieldProps = NumberTaskFieldConfig & TaskFieldContainerProps;

const NumberTaskField = (props: NumberTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, outlinedInputProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        fullWidth
        type='number'
        style={{ maxWidth: MAX_FIELD_WIDTH_SMALL }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='Write your answer here'
        startAdornment={
          <InputAdornment disablePointerEvents position='start'>
            #
          </InputAdornment>
        }
        value={value}
        onChange={onChange}
        required={required}
      />
    </TaskFieldContainer>
  );
};

type MultipleChoiceTaskFieldConfig = {
  options: Array<{
    id: string;
    label: string;
    allowText: boolean;
  }>;
  value: TaskFormikMultipleChoiceType;
  onChange: (event: TaskFormikMultipleChoiceType) => void;
  values: TaskFormValuesType;
} & TaskFieldCommonConfig;

export type MultipleChoiceTaskFieldProps = MultipleChoiceTaskFieldConfig & TaskFieldContainerProps;

const MultipleChoiceTaskField = (props: MultipleChoiceTaskFieldProps) => {
  const { title, error, ariaId, ariaDescribedBy, options, value, onChange, required, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    value.multipleChoiceOptionId = event.target.value;
    onChange(value);
  };

  const toggleOptionalText = (otherSelected: boolean) => {
    value.otherOptionSelected = otherSelected;
  };

  function updateOtherText(event: React.ChangeEvent<HTMLInputElement>) {
    value.otherText = event.target.value;
    onChange(value);
  }

  return (
    <TaskFieldContainer
      title={title}
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      required={required}
      {...rest}
    >
      <>
        <RadioGroup
          id={ariaId}
          name={ariaId}
          aria-describedby={ariaDescribedBy}
          aria-label={title}
          value={value.multipleChoiceOptionId}
          onChange={handleChange}
        >
          {options.map((option) => {
            return (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={
                  <Radio required={required} color='primary' onClick={() => toggleOptionalText(option.allowText)} />
                }
                label={option.label}
              />
            );
          })}
        </RadioGroup>
        {value.otherOptionSelected ? (
          <OutlinedInput
            fullWidth
            style={{ maxWidth: MAX_FIELD_WIDTH_SMALL, marginLeft: 16 }}
            id={ariaId}
            aria-describedby={ariaDescribedBy}
            placeholder='Write your answer here'
            value={value.otherText}
            onChange={updateOtherText}
            required
          />
        ) : null}
      </>
    </TaskFieldContainer>
  );
};

type QuizTaskFieldConfig = {
  options: Array<{
    id: string;
    label: string;
    allowText: boolean;
  }>;
  correctOptionId?: string;
  value: TaskFormikQuizType;
  onChange: (event: TaskFormikQuizType) => void;
} & TaskFieldCommonConfig;

export type QuizTaskFieldProps = QuizTaskFieldConfig & TaskFieldContainerProps;

const QuizTaskField = (props: QuizTaskFieldProps) => {
  const { title, error, ariaId, ariaDescribedBy, options, value, onChange, required, correctOptionId, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    value.quizTaskFieldOptionId = event.target.value;
    value.isCorrect = correctOptionId === event.target.value;
    onChange(value);
  };

  const onSelectOption = (otherSelected: boolean) => {
    value.otherTaskFieldOptionSelected = otherSelected;
  };

  function updateOtherText(event: React.ChangeEvent<HTMLInputElement>) {
    value.otherText = event.target.value;
    onChange(value);
  }

  return (
    <TaskFieldContainer
      title={title}
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      required={required}
      {...rest}
    >
      <>
        <RadioGroup
          id={ariaId}
          name={ariaId}
          aria-describedby={ariaDescribedBy}
          aria-label={title}
          value={value.quizTaskFieldOptionId}
          onChange={handleChange}
        >
          {options.map((option) => {
            return (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio required={required} color='primary' onClick={() => onSelectOption(option.allowText)} />}
                label={option.label}
              />
            );
          })}
        </RadioGroup>
        {value.otherTaskFieldOptionSelected ? (
          <OutlinedInput
            fullWidth
            style={{ maxWidth: MAX_FIELD_WIDTH_SMALL, marginLeft: 16 }}
            id={ariaId}
            aria-describedby={ariaDescribedBy}
            placeholder='Write your answer here'
            value={value.otherText}
            onChange={updateOtherText}
            required
          />
        ) : null}
      </>
    </TaskFieldContainer>
  );
};

type CheckboxTaskFieldConfig = {
  options: Array<{
    id: string;
    label: string;
  }>;
  value: string[];
  onChange: (event: string[]) => void;
} & TaskFieldCommonConfig;

export type CheckboxTaskFieldProps = CheckboxTaskFieldConfig & TaskFieldContainerProps;

const CheckboxTaskField = (props: CheckboxTaskFieldProps) => {
  const { title, error, ariaId, ariaDescribedBy, options, value, onChange, required, ...rest } = props;

  // const addOptionId = (optionId: string) => {
  //   value.push(optionId);
  //   // onChange(value);
  // };
  //
  // const removeOptionId = (optionId: string) => {
  //   value = value.filter((val) => val !== optionId);
  //   // onChange(value);
  // };

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      // addOptionId(event.target.name);
      value.push(event.target.name);
      onChange(value);
      return;
    }

    // removeOptionId(event.target.name);

    onChange(value.filter((val) => val !== event.target.name));
  }

  return (
    <TaskFieldContainer
      title={title}
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      required={required}
      {...rest}
    >
      <FormControl component='fieldset' required={required}>
        <FormGroup id={ariaId} aria-describedby={ariaDescribedBy} aria-label={title}>
          {options.map((option) => {
            const checked = value.find((val) => val === option.id);
            return (
              <FormControlLabel
                key={option.id}
                control={<Checkbox checked={!!checked} onChange={handleChange} color='primary' name={option.id} />}
                label={option.label}
              />
            );
          })}
        </FormGroup>
      </FormControl>
    </TaskFieldContainer>
  );
};

type DropdownTaskFieldConfig = {
  options: Array<{
    id: string;
    label: string;
    allowText: boolean;
  }>;
  value: TaskFormikDropdownType;
  onChange: (value: TaskFormikDropdownType) => void;
  freeSolo?: boolean;
  selectProps?: SelectProps;
} & TaskFieldCommonConfig;

export type DropdownTaskFieldProps = DropdownTaskFieldConfig & TaskFieldContainerProps;

const DropdownTaskField = (props: DropdownTaskFieldProps) => {
  const {
    title,
    error,
    ariaId,
    ariaDescribedBy,
    value,
    onChange,
    options,
    required,
    freeSolo,
    selectProps,
    ...rest
  } = props;

  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedId = event.target.value as string;
    const selectedOption = options.find((o) => o.id === selectedId);
    onChange({
      ...value,
      dropdownOptionId: selectedId,
      otherOptionSelected: selectedOption?.allowText ?? false,
    });
  };

  function updateOtherText(event: React.ChangeEvent<HTMLInputElement>) {
    onChange({ ...value, otherText: event.target.value });
  }

  const selectedOption = options.find((o) => o.id === value.dropdownOptionId) ?? null;

  const inputDisplay = value.otherOptionSelected ? value.otherText || '' : selectedOption?.label || '';
  const [inputValue, setInputValue] = useState(inputDisplay);

  useEffect(() => {
    setInputValue(inputDisplay);
  }, [inputDisplay]);

  if (freeSolo) {
    const nonOtherOptions = options.filter((o) => !o.allowText);

    const handleBlur = () => {
      const trimmed = inputValue.trim();
      if (!trimmed) {
        onChange({ dropdownOptionId: '', otherText: '', otherOptionSelected: false });
        return;
      }
      const match = nonOtherOptions.find((o) => o.label.toLowerCase() === trimmed.toLowerCase());
      if (match) {
        onChange({ dropdownOptionId: match.id, otherText: '', otherOptionSelected: false });
      } else {
        const otherOption = options.find((o) => o.allowText);
        onChange({ dropdownOptionId: otherOption?.id ?? '', otherText: trimmed, otherOptionSelected: true });
      }
    };

    return (
      <TaskFieldContainer
        title={title}
        error={error}
        ariaId={ariaId}
        ariaDescribedBy={ariaDescribedBy}
        required={required}
        {...rest}
      >
        <Autocomplete
          freeSolo
          options={nonOtherOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          inputValue={inputValue}
          onInputChange={(_unusedEvent, newInputValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setInputValue(newInputValue);
            }
          }}
          onChange={(_unusedEvent, newValue) => {
            if (newValue === null) {
              setInputValue('');
              onChange({ dropdownOptionId: '', otherText: '', otherOptionSelected: false });
            } else if (typeof newValue === 'string') {
              setInputValue(newValue);
            } else {
              setInputValue(newValue.label);
              onChange({ dropdownOptionId: newValue.id, otherText: '', otherOptionSelected: false });
            }
          }}
          onBlur={handleBlur}
          ListboxProps={{ style: { maxHeight: 200 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='outlined'
              placeholder='Type to search or enter a custom value'
              required={required}
            />
          )}
          style={{ maxWidth: MAX_FIELD_WIDTH_SMALL }}
        />
      </TaskFieldContainer>
    );
  }

  return (
    <TaskFieldContainer
      title={title}
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      required={required}
      {...rest}
    >
      <>
        <Select
          {...selectProps}
          variant='outlined'
          native
          value={value.dropdownOptionId}
          onChange={handleSelectChange}
          fullWidth
          style={{ maxWidth: MAX_FIELD_WIDTH_SMALL }}
          inputProps={{
            name: ariaId,
            id: ariaId,
          }}
          required={required}
        >
          <option aria-label='None' value=''>
            Select an answer
          </option>
          {options.map((option) => {
            return (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            );
          })}
        </Select>
        {value.otherOptionSelected ? (
          <OutlinedInput
            fullWidth
            style={{ maxWidth: MAX_FIELD_WIDTH_SMALL, marginTop: 8 }}
            id={`${ariaId}-other`}
            aria-describedby={ariaDescribedBy}
            placeholder='Write your answer here'
            value={value.otherText}
            onChange={updateOtherText}
            required
          />
        ) : null}
      </>
    </TaskFieldContainer>
  );
};

type AttachmentTaskFieldConfig = {
  allowMultiple: boolean;
  uploadedFiles: Array<UploadedFile>;
  handleFilesAdded: (files: FugFile[]) => void;
  handleFileRemoved: (file: FugFile) => void;
} & TaskFieldCommonConfig;

export type AttachmentTaskFieldProps = AttachmentTaskFieldConfig & TaskFieldContainerProps;

const AttachmentTaskField = (props: AttachmentTaskFieldProps) => {
  const {
    error,
    ariaId,
    ariaDescribedBy,
    allowMultiple,
    uploadedFiles,
    handleFilesAdded,
    handleFileRemoved,
    required,
    ...rest
  } = props;

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <FileUploadGallery
        allowMultiple={allowMultiple}
        uploadedFiles={uploadedFiles}
        handleFilesAdded={handleFilesAdded}
        handleFileRemoved={handleFileRemoved}
        // required={required}
      />
    </TaskFieldContainer>
  );
};

type DateTaskFieldConfig = {
  value: ParsableDate;
  onChange: (date: MaterialUiPickersDate, value?: string | null | undefined) => void;
  keyboardDatePickerProps?: Omit<KeyboardDatePickerProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type DateTaskFieldProps = DateTaskFieldConfig & TaskFieldContainerProps;

const DateTaskField = (props: DateTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, keyboardDatePickerProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <KeyboardDatePicker
        {...keyboardDatePickerProps}
        autoOk
        variant='inline'
        inputVariant='outlined'
        format='dd/MM/yyyy'
        placeholder='dd/mm/yyyy'
        InputAdornmentProps={{
          position: 'start',
        }}
        KeyboardButtonProps={{ tabIndex: -1 }}
        value={value}
        onChange={onChange}
        required={required}
        openTo='year'
        maxDateMessage=''
        minDateMessage=''
        invalidDateMessage='Date format must be: dd/mm/yyyy'
      />
    </TaskFieldContainer>
  );
};

type TimeTaskFieldConfig = {
  value: ParsableDate;
  onChange: (date: MaterialUiPickersDate, value?: string | null | undefined) => void;
  keyboardTimePickerProps?: Omit<KeyboardTimePickerProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type TimeTaskFieldProps = TimeTaskFieldConfig & TaskFieldContainerProps;

const TimeTaskField = (props: TimeTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, keyboardTimePickerProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <KeyboardTimePicker
        {...keyboardTimePickerProps}
        autoOk
        ampm
        variant='inline'
        inputVariant='outlined'
        placeholder='--:-- am/pm'
        mask='__:__ _M'
        keyboardIcon={<AccessTime fontSize='small' color='action' />}
        InputAdornmentProps={{
          position: 'start',
        }}
        KeyboardButtonProps={{ tabIndex: -1 }}
        value={value}
        onChange={onChange}
        required={required}
        invalidDateMessage='Time format must be: hh:mm am/pm'
        labelFunc={(date) => (date ? date.toLocaleString({ hour: '2-digit', minute: '2-digit', hour12: true }) : '')}
      />
    </TaskFieldContainer>
  );
};

type AddressTaskFieldConfig = {
  allowManual: boolean;
  addressAutocompleteValue?: AddressAutoCompleteType;
  handleAddressAutoCompleteChange: (address: AddressAutoCompleteType) => void;
  manualAddressValue?: AddressManualFieldsType;
  handleAddressManualChange?: (address: AddressManualFieldsType) => void;
} & TaskFieldCommonConfig;

export type AddressTaskFieldProps = AddressTaskFieldConfig & TaskFieldContainerProps;

const AddressTaskField = (props: AddressTaskFieldProps) => {
  const {
    error,
    ariaId,
    ariaDescribedBy,
    autoComplete,
    allowManual,
    addressAutocompleteValue,
    handleAddressAutoCompleteChange,
    manualAddressValue,
    handleAddressManualChange,
    helperText,
    required,
    ...rest
  } = props;
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    if (showManual && handleAddressManualChange && manualAddressValue) handleAddressManualChange(manualAddressValue);
    if (!showManual && handleAddressAutoCompleteChange && addressAutocompleteValue)
      handleAddressAutoCompleteChange(addressAutocompleteValue);
  }, [showManual]);

  const errorMsg = useMemo(() => (showManual ? helperText : error ? 'Required' : ''), [showManual, helperText, error]);

  if (!allowManual) {
    return (
      <TaskFieldContainer
        error={error}
        ariaId={ariaId}
        ariaDescribedBy={ariaDescribedBy}
        helperText={errorMsg}
        required={required}
        {...rest}
      >
        <AddressAutoComplete
          id={ariaId}
          value={addressAutocompleteValue}
          onChange={handleAddressAutoCompleteChange}
          required={required}
          autoComplete={autoComplete}
        />
      </TaskFieldContainer>
    );
  }

  if (handleAddressManualChange === undefined) {
    throw new Error('AddressTaskField requires a handleAddressManualChange if allowManual is true');
  }

  if (manualAddressValue === undefined) {
    throw new Error('AddressTaskField requires a manualAddressValue if allowManual is true');
  }

  return (
    <TaskFieldContainer
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      helperText={errorMsg}
      required={required}
      {...rest}
    >
      <Box maxWidth={MAX_FIELD_WIDTH}>
        {showManual ? (
          <>
            <AddressManualFields value={manualAddressValue} onChange={handleAddressManualChange} required={required} />

            <HoverText style={{ marginTop: 12 }} color='primary' variant='body2' onClick={() => setShowManual(false)}>
              Back to address search
            </HoverText>
          </>
        ) : (
          <>
            <AddressAutoComplete
              id={ariaId}
              value={addressAutocompleteValue}
              autoComplete={autoComplete}
              onChange={handleAddressAutoCompleteChange}
            />

            <HoverText style={{ marginTop: 12 }} color='primary' variant='body2' onClick={() => setShowManual(true)}>
              {`Can't find your address?`}
            </HoverText>
          </>
        )}
      </Box>
    </TaskFieldContainer>
  );
};

type PhoneFieldConfig = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type PhoneTaskFieldProps = PhoneFieldConfig & TaskFieldContainerProps;

const PhoneTaskField = (props: PhoneTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, outlinedInputProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        startAdornment={
          <InputAdornment disablePointerEvents position='start'>
            <PhoneIphoneRounded color='action' />
          </InputAdornment>
        }
        type='tel'
        fullWidth
        style={{ maxWidth: MAX_FIELD_WIDTH }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='Write your answer here'
        value={value}
        onChange={onChange}
        required={required}
      />
    </TaskFieldContainer>
  );
};

type EmailTaskFieldConfig = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type EmailTaskFieldProps = EmailTaskFieldConfig & TaskFieldContainerProps;

const EmailTaskField = (props: EmailTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, outlinedInputProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        startAdornment={
          <InputAdornment disablePointerEvents position='start'>
            <AlternateEmailRounded color='action' />
          </InputAdornment>
        }
        type='email'
        fullWidth
        style={{ maxWidth: MAX_FIELD_WIDTH }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='Write your answer here'
        value={value}
        onChange={onChange}
        required={required}
      />
    </TaskFieldContainer>
  );
};

type UrlTaskFieldConfig = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  outlinedInputProps?: Omit<OutlinedInputProps, 'onChange' | 'value'>;
} & TaskFieldCommonConfig;

export type UrlTaskFieldProps = UrlTaskFieldConfig & TaskFieldContainerProps;

const UrlTaskField = (props: UrlTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, outlinedInputProps, ...rest } = props;
  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <OutlinedInput
        {...outlinedInputProps}
        startAdornment={
          <InputAdornment disablePointerEvents position='start'>
            <LinkIcon color='action' />
          </InputAdornment>
        }
        type='url'
        fullWidth
        style={{ maxWidth: MAX_FIELD_WIDTH }}
        id={ariaId}
        aria-describedby={ariaDescribedBy}
        placeholder='https://example.com'
        value={value}
        onChange={onChange}
        required={required}
      />
    </TaskFieldContainer>
  );
};

type YesNoTaskFieldConfig = {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
} & TaskFieldCommonConfig;

export type YesNoTaskFieldProps = YesNoTaskFieldConfig & TaskFieldContainerProps;

const YesNoTaskField = (props: YesNoTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, ...rest } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value === 'yes');
  };

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <RadioGroup
        id={ariaId}
        name={ariaId}
        aria-describedby={ariaDescribedBy}
        value={value === null ? '' : value ? 'yes' : 'no'}
        onChange={handleChange}
        row
      >
        <FormControlLabel value='yes' control={<Radio color='primary' required={required} />} label='Yes' />
        <FormControlLabel value='no' control={<Radio color='primary' required={required} />} label='No' />
      </RadioGroup>
    </TaskFieldContainer>
  );
};

type SignatureTaskFieldConfig = {
  value: string;
  onChangeSignature: (file: string) => void;
} & TaskFieldCommonConfig;

export type SignatureTaskFieldProps = SignatureTaskFieldConfig & TaskFieldContainerProps;

const SignatureTaskField = (props: SignatureTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChangeSignature, required, ...rest } = props;

  return (
    <TaskFieldContainer
      required={required}
      error={error}
      ariaId={ariaId}
      ariaDescribedBy={ariaDescribedBy}
      pl={2}
      {...rest}
    >
      <SignaturePad value={value} onChange={onChangeSignature} />
    </TaskFieldContainer>
  );
};

type RatingTaskFieldConfig = {
  value: number;
  onChange: (event: React.ChangeEvent<{}>, value: number | null) => void;
} & TaskFieldCommonConfig;

export type RatingTaskFieldProps = RatingTaskFieldConfig & TaskFieldContainerProps;

const RatingTaskField = (props: RatingTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, ...rest } = props;

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <Rating id={ariaId} aria-describedby={ariaDescribedBy} size='large' value={value || 0} onChange={onChange} />
    </TaskFieldContainer>
  );
};

type LinearScaleTaskFieldConfig = {
  value: number | null;
  onChange: (value: number | null) => void;
  minValue?: number;
  maxValue?: number;
  minLabel?: string;
  maxLabel?: string;
} & TaskFieldCommonConfig;

export type LinearScaleTaskFieldProps = LinearScaleTaskFieldConfig & TaskFieldContainerProps;

const generateMarks = (min: number, max: number) => {
  const marks = [];
  for (let i = min; i <= max; i++) {
    marks.push({ value: i, label: String(i) });
  }
  return marks;
};

const LinearScaleTaskField = (props: LinearScaleTaskFieldProps) => {
  const {
    error,
    ariaId,
    ariaDescribedBy,
    value,
    onChange,
    required,
    minValue = 1,
    maxValue = 5,
    minLabel,
    maxLabel,
    ...rest
  } = props;

  const marks = useMemo(() => generateMarks(minValue, maxValue), [minValue, maxValue]);

  const handleChange = (_unusedEvent: React.ChangeEvent<{}>, newValue: number | number[]) => {
    onChange(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <Box style={{ maxWidth: MAX_FIELD_WIDTH, paddingLeft: 8, paddingRight: 8 }}>
        {(minLabel || maxLabel) && (
          <Box display='flex' justifyContent='space-between' mb={1}>
            <Typography variant='caption' color='textSecondary'>
              {minLabel}
            </Typography>
            <Typography variant='caption' color='textSecondary'>
              {maxLabel}
            </Typography>
          </Box>
        )}
        <Slider
          id={ariaId}
          aria-describedby={ariaDescribedBy}
          value={value ?? minValue}
          onChange={handleChange}
          min={minValue}
          max={maxValue}
          step={1}
          marks={marks}
          valueLabelDisplay='auto'
        />
      </Box>
    </TaskFieldContainer>
  );
};

type NpsTaskFieldConfig = {
  value: number | null;
  onChange: (value: number | null) => void;
} & TaskFieldCommonConfig;

export type NpsTaskFieldProps = NpsTaskFieldConfig & TaskFieldContainerProps;

const npsMarks = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

const NpsTaskField = (props: NpsTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, ...rest } = props;

  const handleChange = (_unusedEvent: React.ChangeEvent<{}>, newValue: number | number[]) => {
    onChange(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <Box style={{ maxWidth: MAX_FIELD_WIDTH, paddingLeft: 8, paddingRight: 8 }}>
        <Box display='flex' justifyContent='space-between' mb={1}>
          <Typography variant='caption' color='textSecondary'>
            Not at all likely
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            Extremely likely
          </Typography>
        </Box>
        <Slider
          id={ariaId}
          aria-describedby={ariaDescribedBy}
          value={value ?? 0}
          onChange={handleChange}
          min={0}
          max={10}
          step={1}
          marks={npsMarks}
          valueLabelDisplay='auto'
        />
      </Box>
    </TaskFieldContainer>
  );
};

type MatrixRow = {
  matrixRowId: string;
  name: string;
  order: number;
};

type MatrixColumn = {
  matrixColumnId: string;
  name: string;
  order: number;
};

type MatrixTaskFieldConfig = {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  rows: MatrixRow[];
  columns: MatrixColumn[];
} & TaskFieldCommonConfig;

export type MatrixTaskFieldProps = MatrixTaskFieldConfig & TaskFieldContainerProps;

const MatrixTaskField = (props: MatrixTaskFieldProps) => {
  const { error, ariaId, ariaDescribedBy, value, onChange, required, rows, columns, ...rest } = props;

  const sortedRows = useMemo(() => [...rows].sort((a, b) => a.order - b.order), [rows]);
  const sortedColumns = useMemo(() => [...columns].sort((a, b) => a.order - b.order), [columns]);

  const handleChange = (rowId: string, columnId: string) => {
    onChange({ ...value, [rowId]: columnId });
  };

  return (
    <TaskFieldContainer required={required} error={error} ariaId={ariaId} ariaDescribedBy={ariaDescribedBy} {...rest}>
      <Box overflow='auto'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell style={{ borderBottom: 'none', minWidth: 120 }} />
              {sortedColumns.map((column) => (
                <TableCell
                  key={column.matrixColumnId}
                  align='center'
                  style={{ borderBottom: 'none', padding: '8px 12px' }}
                >
                  <Typography variant='body2' color='textSecondary'>
                    {column.name}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.matrixRowId}>
                <TableCell style={{ borderBottom: 'none', padding: '8px 12px' }}>
                  <Typography variant='body2'>{row.name}</Typography>
                </TableCell>
                {sortedColumns.map((column) => (
                  <TableCell
                    key={column.matrixColumnId}
                    align='center'
                    style={{ borderBottom: 'none', padding: '4px 12px' }}
                  >
                    <Radio
                      checked={value[row.matrixRowId] === column.matrixColumnId}
                      onChange={() => handleChange(row.matrixRowId, column.matrixColumnId)}
                      size='small'
                      color='primary'
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </TaskFieldContainer>
  );
};

export {
  ShortTextTaskField,
  LongTextTaskField,
  NumberTaskField,
  MultipleChoiceTaskField,
  CheckboxTaskField,
  DropdownTaskField,
  AttachmentTaskField,
  DateTaskField,
  TimeTaskField,
  AddressTaskField,
  PhoneTaskField,
  EmailTaskField,
  UrlTaskField,
  YesNoTaskField,
  QuizTaskField,
  SignatureTaskField,
  RatingTaskField,
  LinearScaleTaskField,
  NpsTaskField,
  MatrixTaskField,
};
